import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
  CreditCardIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import api from '../services/api.ts';

interface DebtFormData {
  creditorName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category:
    | 'loan'
    | 'financing'
    | 'account_payable'
    | 'consortium'
    | 'real_estate'
    | 'vehicle'
    | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
}

const CreateDebt: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<DebtFormData>({
    creditorName: '',
    description: '',
    originalAmount: 0,
    currentAmount: 0,
    interestRate: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'active',
    category: 'other',
    paymentMethod: '',
    installments: 1,
    currentInstallment: 1,
  });

  const [originalAmountDisplay, setOriginalAmountDisplay] = useState('');
  const [currentAmountDisplay, setCurrentAmountDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createDebtMutation = useMutation(
    async (data: DebtFormData) => {
      const response = await api.post('/debts', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('debts');
        navigate('/debts');
      },
      onError: (error: any) => {
        console.error('Erro ao criar dívida:', error);
        setErrors({ submit: 'Erro ao criar dívida. Tente novamente.' });
      },
    }
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        name === 'interestRate' || name === 'installments' || name === 'currentInstallment'
          ? Number(value)
          : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCurrencyChange = (field: 'originalAmount' | 'currentAmount', value: string) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'originalAmount') {
      setOriginalAmountDisplay(maskedValue);
      setFormData(prev => ({ ...prev, originalAmount: numericValue }));

      // Auto-set currentAmount when originalAmount changes (if currentAmount is 0)
      if (formData.currentAmount === 0) {
        setCurrentAmountDisplay(maskedValue);
        setFormData(prev => ({ ...prev, currentAmount: numericValue }));
      }
    } else {
      setCurrentAmountDisplay(maskedValue);
      setFormData(prev => ({ ...prev, currentAmount: numericValue }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.creditorName.trim()) newErrors.creditorName = 'Nome do credor é obrigatório';
    if (!formData.originalAmount || formData.originalAmount <= 0) {
      newErrors.originalAmount = 'Valor original deve ser maior que zero';
    }
    if (formData.currentAmount < 0) {
      newErrors.currentAmount = 'Saldo devedor não pode ser negativo';
    }
    if (formData.interestRate < 0 || formData.interestRate > 100) {
      newErrors.interestRate = 'Taxa de juros deve estar entre 0% e 100%';
    }
    if (!formData.dueDate) newErrors.dueDate = 'Data de vencimento é obrigatória';
    if (formData.installments && formData.installments < 1) {
      newErrors.installments = 'Número de parcelas deve ser maior que zero';
    }
    if (
      formData.currentInstallment &&
      formData.installments &&
      formData.currentInstallment > formData.installments
    ) {
      newErrors.currentInstallment = 'Parcela atual não pode ser maior que o total de parcelas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      createDebtMutation.mutate(formData);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/debts')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Dívidas
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Criar Nova Dívida</h1>
          <p className="text-gray-600 mt-2">Adicione uma nova dívida ao seu controle financeiro</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="creditorName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Nome do Credor *
                </label>
                <input
                  type="text"
                  name="creditorName"
                  id="creditorName"
                  value={formData.creditorName}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.creditorName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Banco do Brasil, Nubank, Loja XYZ"
                />
                {errors.creditorName && (
                  <p className="text-red-500 text-sm mt-1">{errors.creditorName}</p>
                )}
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  name="category"
                  id="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="loan">Empréstimo</option>
                  <option value="financing">Financiamento</option>
                  <option value="account_payable">Conta a Pagar</option>
                  <option value="consortium">Consórcio</option>
                  <option value="real_estate">Imóvel</option>
                  <option value="vehicle">Veículo</option>
                  <option value="other">Outros</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="originalAmount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor Original *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="originalAmount"
                    id="originalAmount"
                    value={originalAmountDisplay}
                    onChange={e => handleCurrencyChange('originalAmount', e.target.value)}
                    required
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.originalAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.originalAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.originalAmount}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="currentAmount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Saldo Devedor *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="currentAmount"
                    id="currentAmount"
                    value={currentAmountDisplay}
                    onChange={e => handleCurrencyChange('currentAmount', e.target.value)}
                    required
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.currentAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.currentAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.currentAmount}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="interestRate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Taxa de Juros (% ao mês)
                </label>
                <input
                  type="number"
                  name="interestRate"
                  id="interestRate"
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.interestRate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.interestRate && (
                  <p className="text-red-500 text-sm mt-1">{errors.interestRate}</p>
                )}
              </div>

              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Vencimento *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.dueDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  id="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Ativa</option>
                  <option value="overdue">Em Atraso</option>
                  <option value="renegotiated">Renegociada</option>
                  <option value="paid">Paga</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="paymentMethod"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Método de Pagamento
                </label>
                <input
                  type="text"
                  name="paymentMethod"
                  id="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Débito automático, Boleto, PIX"
                />
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descreva a dívida (ex: Compra de móveis, Empréstimo pessoal, etc.)"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/debts')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createDebtMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {createDebtMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Criando...
                </>
              ) : (
                'Criar Dívida'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDebt;
